import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import type { ISelectedFolder } from '@/store/types';
import type {
  ICategoryRoute,
  IClientRoute,
  IDatabaseRoute,
  IFolderRoute,
  IPreviewDatabaseItemRoute,
  IPreviewRoute,
  IRoute,
  IViewRoute,
} from './types';

import routes from '@/config/routes';
import { defaultFolderHome } from '@/constants';
import { ResourceTypeId } from '@/store/enums';

const getRouteInfo = (currentUrl: string): IRoute | null => {
  const match = matchPath<IRoute['params']>(currentUrl, {
    path: Object.values(routes.sp.scheduling.resources),
    exact: true,
  });

  if (!match) return null;

  const route = Object.entries(routes.sp.scheduling.resources).find(([, path]) => match.path === path);

  if (!route) return null;

  switch (route[0]) {
    case 'index':
      return { id: 'index', params: {} };
    case 'client': {
      const params = match.params as IClientRoute['params'];
      return { id: 'client', params };
    }
    case 'category': {
      const params = match.params as ICategoryRoute['params'];
      return { id: 'category', params };
    }
    case 'database': {
      const params = match.params as IDatabaseRoute['params'];
      return { id: 'database', params };
    }
    case 'folder': {
      const params = match.params as IFolderRoute['params'];
      return { id: 'folder', params };
    }
    case 'view': {
      const params = match.params as IViewRoute['params'];
      return { id: 'view', params };
    }
    case 'preview': {
      const params = match.params as IPreviewRoute['params'];
      return { id: 'preview', params };
    }
    case 'previewDatabaseItem': {
      const params = match.params as IPreviewDatabaseItemRoute['params'];
      return { id: 'previewDatabaseItem', params };
    }
  }

  return null;
};

const getSelectedFolder = (route: IRoute | null): ISelectedFolder => {
  if (!route) {
    return defaultFolderHome;
  }

  switch (route.id) {
    case 'index':
      return defaultFolderHome;
    case 'client':
      return {
        clientId: Number(route.params.client),
        folderId: Number(route.params.client),
        folderType: 'client',
      };
    case 'category': {
      return {
        clientId: Number(route.params.client),
        folderId: ResourceTypeId[route.params.resourceType],
        folderType: 'category',
      };
    }
    case 'folder': {
      return {
        clientId: Number(route.params.client),
        folderId: Number(route.params.folderId),
        folderType: route.params.resourceType,
      };
    }
    case 'database': {
      return {
        clientId: Number(route.params.client),
        folderId: Number(route.params.dbId),
        folderType: 'database',
      };
    }
    case 'view': {
      return {
        clientId: Number(route.params.client),
        folderId: Number(route.params.dbId),
        viewId: Number(route.params.viewId),
        folderType: 'view',
      };
    }
    case 'preview': {
      return {
        clientId: Number(route.params.client),
        folderId: Number(route.params.id),
        folderType: route.params.resourceType,
      };
    }
    case 'previewDatabaseItem': {
      return {
        clientId: Number(route.params.client),
        folderId: Number(route.params.id),
        viewId: Number(route.params.viewId),
        folderType: 'database',
      };
    }
  }
};

interface IResourceNavigation {
  selectedFolder: ISelectedFolder;
}

const useResourceNavigation = (): IResourceNavigation => {
  const { pathname } = useLocation();
  const selectedFolder = useMemo(() => {
    const routeInfo = getRouteInfo(pathname);
    const selectedFolder = getSelectedFolder(routeInfo);

    return selectedFolder;
  }, [pathname]);

  return { selectedFolder };
};

export { useResourceNavigation };
